import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { openFile } from "@/lib/fileViewer";
import { fmtDate, fmtDateShort, fmtTimeRange } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StudentFormDialog from "@/components/StudentFormDialog";
import { StudentAttendanceTab } from "@/components/attendance/StudentAttendanceTab";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, Upload, FileText, File as FileIcon, FileDown,
  Mail, Phone, Calendar, MapPin, IdCard, Camera, GraduationCap, BarChart3, Eye, EyeOff, Award, Presentation,
} from "lucide-react";
import { YEAR_LABEL, TRACK_SHORT, TRACK_LABEL, trackDurationYears } from "@/lib/school";

const STATUS_STYLES = {
  attivo: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  sospeso: "bg-orange-50 text-orange-800 border border-orange-200",
  ritirato: "bg-stone-200 text-stone-700 border border-stone-300",
  diplomato: "bg-indigo-50 text-indigo-800 border border-indigo-200",
};

function fullName(s) {
  return `${s?.first_name || ""} ${s?.last_name || ""}`.trim() || "—";
}
function initials(s) {
  const n = fullName(s);
  return n.split(" ").filter(Boolean).slice(0, 2).map(x => x[0]).join("").toUpperCase() || "•";
}

export default function StudentDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const listBase = useLocation().pathname.startsWith("/modulare") ? "/modulare/studenti" : "/scuola/studenti";
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);

  const { data: student, isLoading } = useQuery({
    queryKey: ["student", id],
    queryFn: async () => (await api.get(`/students/${id}`)).data,
  });

  useEffect(() => {
    let revoked = false;
    if (student?.photo_path) {
      api.get(`/students/${id}/photo`, { responseType: "blob" })
        .then((r) => {
          if (!revoked) setPhotoUrl(URL.createObjectURL(r.data));
        }).catch(() => {});
    } else {
      setPhotoUrl(null);
    }
    return () => { revoked = true; if (photoUrl) URL.revokeObjectURL(photoUrl); };
  }, [student?.photo_path, id]);

  if (isLoading || !student) return <div className="p-10 text-stone-500 text-sm">Caricamento…</div>;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["student", id] });
    qc.invalidateQueries({ queryKey: ["students"] });
    qc.invalidateQueries({ queryKey: ["students-stats"] });
    qc.invalidateQueries({ queryKey: ["student-documents", id] });
  };

  const editMut = async (payload) => {
    try {
      await api.patch(`/students/${id}`, payload);
      toast.success("Studente aggiornato");
      setEditOpen(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/students/${id}`);
      toast.success("Studente eliminato");
      qc.invalidateQueries({ queryKey: ["students"] });
      qc.invalidateQueries({ queryKey: ["students-stats"] });
      navigate(listBase);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
      setDeleteOpen(false);
    }
  };

  async function uploadPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/students/${id}/photo`, fd);
      toast.success("Foto aggiornata");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    }
  }

  async function removePhoto() {
    if (!window.confirm("Rimuovere la foto?")) return;
    await api.delete(`/students/${id}/photo`);
    toast.success("Foto rimossa");
    refresh();
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to={listBase} data-testid="student-back-link" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4">
        <ArrowLeft size={13}/> Torna a studenti
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
        <div className="flex gap-5 items-start">
          <div className="relative shrink-0">
            <div className="w-24 h-24 rounded-2xl overflow-hidden bg-[#1C352D] text-white grid place-items-center text-2xl font-semibold">
              {photoUrl ? (
                <img src={photoUrl} alt={fullName(student)} className="w-full h-full object-cover"/>
              ) : initials(student)}
            </div>
            <label className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[#E2725B] text-white grid place-items-center cursor-pointer shadow-sm hover:bg-[#c85d47] transition-colors duration-150" title="Cambia foto">
              <Camera size={14}/>
              <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={uploadPhoto} data-testid="student-photo-input"/>
            </label>
            {student.photo_path && (
              <button type="button" onClick={removePhoto} className="block mx-auto mt-3 text-[11px] text-stone-500 hover:text-red-600" data-testid="student-photo-remove">Rimuovi foto</button>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-3xl font-semibold tracking-tight text-[#1C352D]">{fullName(student)}</h1>
              <Badge className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATUS_STYLES[student.status] || STATUS_STYLES.attivo}`}>
                {student.status}
              </Badge>
              {student.year_level && (
                <Badge className="rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-[#1C352D] text-white">
                  {YEAR_LABEL[student.year_level]}
                </Badge>
              )}
              {student.track_type && (
                <Badge className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${student.track_type === "residenziale" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-orange-50 text-orange-800 border-orange-200"}`}>
                  {TRACK_SHORT[student.track_type]}
                </Badge>
              )}
            </div>
            <div className="text-sm text-stone-500 mt-1 mono">{student.matricola}</div>
            <div className="flex gap-4 flex-wrap text-xs text-stone-600 mt-3">
              {student.email && <span className="inline-flex items-center gap-1"><Mail size={12}/>{student.email}</span>}
              {student.phone && <span className="inline-flex items-center gap-1"><Phone size={12}/>{student.phone}</span>}
              {student.date_of_birth && <span className="inline-flex items-center gap-1"><Calendar size={12}/>{fmtDate(student.date_of_birth)}</span>}
              {(student.city || student.country) && <span className="inline-flex items-center gap-1"><MapPin size={12}/>{student.city || ""}{student.city && student.country ? ", " : ""}{student.country || ""}</span>}
              {student.codice_fiscale && <span className="inline-flex items-center gap-1 mono uppercase"><IdCard size={12}/>{student.codice_fiscale}</span>}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button data-testid="student-detail-stats" variant="outline" className="rounded-full" onClick={() => navigate(`${listBase}/${id}/statistiche`)}>
            <BarChart3 size={14} className="mr-1.5"/> Statistiche
          </Button>
          <Button data-testid="student-detail-edit" variant="outline" className="rounded-full" onClick={() => setEditOpen(true)}>
            <Pencil size={14} className="mr-1.5"/> Modifica
          </Button>
          <Button data-testid="student-detail-delete" variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 hover:border-red-300" onClick={() => setDeleteOpen(true)}>
            <Trash2 size={14} className="mr-1.5"/> Elimina
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <Tabs defaultValue="anagrafica">
            <TabsList className="bg-white border border-stone-200 max-w-full overflow-x-auto justify-start">
              <TabsTrigger value="anagrafica" data-testid="tab-anagrafica">Anagrafica</TabsTrigger>
              <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti</TabsTrigger>
              <TabsTrigger value="attestati" data-testid="tab-attestati">Attestati</TabsTrigger>
              <TabsTrigger value="presenze" data-testid="tab-presenze">Presenze</TabsTrigger>
              <TabsTrigger value="carriera" data-testid="tab-carriera">Carriera</TabsTrigger>
              <TabsTrigger value="note" data-testid="tab-note">Note</TabsTrigger>
            </TabsList>

            <TabsContent value="anagrafica" className="mt-4">
              <AnagraficaCard student={student}/>
            </TabsContent>
            <TabsContent value="documenti" className="mt-4">
              <DocumentsCard studentId={id}/>
            </TabsContent>
            <TabsContent value="attestati" className="mt-4">
              <CertificatesCard studentId={id}/>
            </TabsContent>
            <TabsContent value="presenze" className="mt-4">
              <StudentAttendanceTab student={student}/>
            </TabsContent>
            <TabsContent value="carriera" className="mt-4">
              <CarrieraCard studentId={id}/>
            </TabsContent>
            <TabsContent value="note" className="mt-4">
              <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
                <div className="text-sm font-medium mb-2">Note</div>
                <div className="text-sm text-stone-700 whitespace-pre-wrap min-h-[80px]">
                  {student.notes || <span className="text-stone-400">Nessuna nota inserita.</span>}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <aside className="space-y-5">
          <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3">Percorso</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-stone-500">Tipologia</span>
                <span className="font-medium">{TRACK_LABEL[student.track_type] || "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500">Aula</span>
                <span className="font-medium">{student.year_level ? YEAR_LABEL[student.year_level] : "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500">Annualità</span>
                <span className="font-medium mono">{student.academic_year || "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500">Durata totale</span>
                <span className="font-medium">{trackDurationYears(student.track_type) ? `${trackDurationYears(student.track_type)} anni` : "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500">Indirizzo</span>
                <span className="font-medium">{student.course_track || "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-500">Iscrizione</span>
                <span className="font-medium">{fmtDate(student.enrollment_date)}</span>
              </div>
            </div>
          </Card>

          {(student.guardian_name || student.guardian_email || student.guardian_phone) && (
            <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3">Contatto di riferimento</div>
              <div className="text-sm space-y-1">
                {student.guardian_name && <div className="font-medium">{student.guardian_name}</div>}
                {student.guardian_email && <div className="text-stone-600 inline-flex items-center gap-1"><Mail size={12}/>{student.guardian_email}</div>}
                {student.guardian_phone && <div className="text-stone-600 inline-flex items-center gap-1"><Phone size={12}/>{student.guardian_phone}</div>}
              </div>
            </Card>
          )}

          {student.chiesa_provenienza && (
            <Card className="p-6 border border-stone-200 rounded-xl shadow-none" data-testid="student-chiesa-card">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Chiesa di provenienza</div>
              <div className="text-sm font-medium">{student.chiesa_provenienza}</div>
            </Card>
          )}

          {(student.dsa || student.health_notes || student.intolerances) && (
            <Card className="p-6 border border-amber-200 bg-amber-50/40 rounded-xl shadow-none" data-testid="student-health-card">
              <div className="text-[10px] uppercase tracking-widest text-amber-800 mb-3">Note sanitarie (riservate)</div>
              <div className="text-sm space-y-2">
                {student.dsa && (
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-amber-300 px-2.5 py-0.5 text-[11px] font-medium text-amber-900">
                    DSA
                  </div>
                )}
                {student.health_notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Salute</div>
                    <div className="whitespace-pre-wrap text-stone-800">{student.health_notes}</div>
                  </div>
                )}
                {student.intolerances && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Intolleranze / allergie</div>
                    <div className="whitespace-pre-wrap text-stone-800">{student.intolerances}</div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </aside>
      </div>

      <StudentFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        initialStudent={student}
        onSubmit={editMut}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {fullName(student)}?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;operazione è irreversibile. La scheda studente verrà rimossa;
              i documenti caricati resteranno archiviati come cancellati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="student-detail-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="student-detail-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={doDelete}
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AnagraficaCard({ student }) {
  const rows = [
    ["Nome", student.first_name],
    ["Cognome", student.last_name],
    ["Data di nascita", fmtDate(student.date_of_birth)],
    ["Luogo di nascita", student.birth_place],
    ["Genere", student.gender],
    ["Codice fiscale", student.codice_fiscale],
    ["Assicurazione", student.has_insurance ? "Presente" : "Assente"],
    ["Email", student.email],
    ["Telefono", student.phone],
    ["Indirizzo", student.address],
    ["Città", student.city],
    ["Paese", student.country],
  ];
  return (
    <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
      <div className="text-sm font-medium mb-4">Dati personali</div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-stone-100 py-2 text-sm">
            <dt className="text-stone-500">{k}</dt>
            <dd className="font-medium text-right">{v || <span className="text-stone-400">—</span>}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function CarrieraCard({ studentId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["student-attendance-history", studentId],
    queryFn: async () => (await api.get(`/attendance/students/${studentId}/history`)).data,
  });
  const { data: coursePct = [] } = useQuery({
    queryKey: ["student-attendance-percentages", studentId],
    queryFn: async () => (await api.get(`/attendance/students/${studentId}/course-percentages`)).data,
  });
  if (isLoading) return <Card className="p-6 border border-stone-200 rounded-xl shadow-none text-sm text-stone-500">Caricamento presenze…</Card>;
  const total = data?.total || 0;
  const c = data?.counts || {};
  const rate = data?.rate || 0;
  const records = data?.records || [];
  const badgeFor = {
    presente: "bg-emerald-50 text-emerald-800 border-emerald-200",
    assente:  "bg-red-50 text-red-800 border-red-200",
    ritardo:  "bg-orange-50 text-orange-800 border-orange-200",
    giustificato: "bg-stone-100 text-stone-700 border-stone-200",
  };
  return (
    <div className="space-y-4">
      <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium">Presenze</div>
          <div className="text-2xl font-semibold">{rate.toFixed(0)}%</div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            ["presente", "Presenti", "text-emerald-700"],
            ["assente",  "Assenze",  "text-red-700"],
            ["ritardo",  "Ritardi",  "text-orange-700"],
            ["giustificato", "Giustificate", "text-stone-700"],
          ].map(([k, l, cn]) => (
            <div key={k} className="text-center">
              <div className={`text-2xl font-semibold ${cn}`}>{c[k] || 0}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">{l}</div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-stone-500 mt-4">Totale sessioni registrate: {total}</div>
      </Card>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 border-b border-stone-100 text-sm font-medium flex items-center justify-between">
          <span>Frequenza per corso (ore)</span>
          <span className="text-[11px] text-stone-500 font-normal">Calcolata sul monte ore totale del corso</span>
        </div>
        {coursePct.length === 0 ? (
          <div className="p-6 text-sm text-stone-500">Nessuna presenza per corso registrata.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {coursePct.map((c) => {
              const pct = c.attendance_percentage || 0;
              const barColor = pct >= 75 ? "#16a34a" : pct >= 50 ? "#f59e0b" : "#dc2626";
              return (
                <li key={c.course_id} className="p-4" data-testid={`course-pct-${c.course_id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="font-medium text-[#1C352D] truncate flex items-center gap-2">
                        {c.course_name}
                        {c.is_seminar && <Badge className="bg-[#E2725B]/10 text-[#8f3e26] border border-[#E2725B]/30 text-[10px] rounded-full">SEMINARIO</Badge>}
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5">
                        {c.attended_hours}h su {c.total_course_hours}h · {c.late_hours ? `${c.late_hours}h ritardo · ` : ""}{c.absent_hours ? `${c.absent_hours}h assenza` : ""}
                      </div>
                    </div>
                    <div className="text-lg font-semibold" style={{ color: barColor }}>{pct}%</div>
                  </div>
                  <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: barColor }}/>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 border-b border-stone-100 text-sm font-medium">Storico sessioni</div>
        {records.length === 0 ? (
          <div className="p-8 text-center text-sm text-stone-500">
            Nessuna presenza registrata. Crea sessioni nel modulo <Link to="/scuola/presenze" className="text-[#E2725B] underline">Presenze</Link>.
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {records.slice(0, 50).map((r) => (
              <li key={r.id} className="p-3 flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <Link to={`/scuola/presenze/${r.session_id}`} className="font-medium hover:underline">{r.session_title || "—"}</Link>
                  <div className="text-[11px] text-stone-500">
                    <span data-testid={`history-when-${r.id}`}>{fmtDateShort(r.session_date)}{r.session_start_time ? ` · ${fmtTimeRange(r.session_start_time, r.session_end_time)}` : ""}</span>{r.arrival_time ? ` · arrivo ${r.arrival_time}` : ""}{r.note ? ` · ${r.note}` : ""}
                  </div>
                </div>
                <Badge className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${badgeFor[r.status] || badgeFor.presente}`}>
                  {r.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function DocumentsCard({ studentId }) {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("Documento");
  const [visibleToStudent, setVisibleToStudent] = useState(true);
  const { data: docs = [] } = useQuery({
    queryKey: ["student-documents", studentId],
    queryFn: async () => (await api.get(`/students/${studentId}/documents`)).data,
  });

  async function onUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label || "Documento");
      fd.append("visible_to_student", visibleToStudent ? "true" : "false");
      await api.post(`/students/${studentId}/documents`, fd);
      toast.success("Documento caricato");
      qc.invalidateQueries({ queryKey: ["student-documents", studentId] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  }

  const del = useMutation({
    mutationFn: async (docId) => (await api.delete(`/students/documents/${docId}`)).data,
    onSuccess: () => { toast.success("Documento eliminato"); qc.invalidateQueries({ queryKey: ["student-documents", studentId] }); },
  });

  const toggleVisibility = useMutation({
    mutationFn: async ({ docId, visible }) =>
      (await api.patch(`/students/documents/${docId}/visibility`, { visible_to_student: visible })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["student-documents", studentId] }); },
    onError: (err) => toast.error(err.response?.data?.detail || "Errore aggiornamento"),
  });

  function openDoc(doc) {
    const token = localStorage.getItem("token");
    const base = process.env.REACT_APP_BACKEND_URL;
    openFile(`${base}/api/students/documents/${doc.id}/download?auth=${encodeURIComponent(token)}`, doc.original_filename);
  }
  function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
  function iconFor(ext) {
    if (ext === "pdf") return <FileText size={18} className="text-red-600"/>;
    if (["png","jpg","jpeg","webp"].includes(ext)) return <FileIcon size={18} className="text-emerald-700"/>;
    return <FileIcon size={18} className="text-stone-500"/>;
  }

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none">
      <div className="p-4 border-b border-stone-100 space-y-3">
        <div className="text-sm font-medium">Documenti studente</div>
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <div className="flex-1">
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Etichetta</Label>
            <Input data-testid="student-doc-label" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1" placeholder="Es. Contratto, Certificato medico, Diploma…"/>
          </div>
          <label className="cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-[#1C352D] hover:bg-[#264A3E] text-white px-4 h-10 text-sm transition-colors duration-150 md:mt-6 md:self-end">
            <Upload size={14}/> {uploading ? "Caricamento…" : "Carica documento"}
            <input data-testid="student-doc-upload" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={onUpload} disabled={uploading}/>
          </label>
        </div>
        <label className="flex items-center gap-2 text-[12px] text-stone-600 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="student-doc-visible-to-student"
            checked={visibleToStudent}
            onChange={(e) => setVisibleToStudent(e.target.checked)}
            className="w-4 h-4 accent-[#1C352D]"
          />
          Visibile allo studente nel portale
        </label>
        <div className="text-[11px] text-stone-500">
          Formati: PDF, PNG, JPG, JPEG, WEBP · Max 15 MB per file.
        </div>
      </div>
      <div className="divide-y divide-stone-100">
        {docs.length === 0 && (
          <div className="p-6 text-sm text-stone-500">Nessun documento caricato per questo studente.</div>
        )}
        {docs.map((d) => {
          const isVisible = d.visible_to_student !== false;
          return (
            <div key={d.id} className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-stone-50 border border-stone-200 grid place-items-center shrink-0">{iconFor(d.extension)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate flex items-center gap-2">
                  {d.label}
                  {d.uploaded_by_student && (
                    <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] rounded-full" data-testid={`doc-by-student-${d.id}`}>Caricato dallo studente</Badge>
                  )}
                  {isVisible ? (
                    <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] rounded-full">Visibile</Badge>
                  ) : (
                    <Badge className="bg-stone-100 text-stone-600 border border-stone-200 text-[10px] rounded-full">Privato</Badge>
                  )}
                </div>
                <div className="text-[11px] text-stone-500 truncate">
                  {d.original_filename} · {formatSize(d.size)} · caricato {fmtDate(d.uploaded_at)}
                </div>
              </div>
              <button
                onClick={() => toggleVisibility.mutate({ docId: d.id, visible: !isVisible })}
                className="text-stone-500 hover:text-[#1C352D] p-2 rounded-full hover:bg-stone-50 transition-colors duration-150"
                title={isVisible ? "Nascondi allo studente" : "Rendi visibile allo studente"}
                data-testid={`student-doc-toggle-visibility-${d.id}`}
              >
                {isVisible ? <Eye size={15}/> : <EyeOff size={15}/>}
              </button>
              <button onClick={() => openDoc(d)} className="text-stone-500 hover:text-[#1C352D] p-2 rounded-full hover:bg-stone-50 transition-colors duration-150" title="Apri">
                <FileDown size={15}/>
              </button>
              <button onClick={() => { if (window.confirm(`Eliminare "${d.label}"?`)) del.mutate(d.id); }} className="text-stone-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors duration-150" title="Elimina">
                <Trash2 size={15}/>
              </button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function CertificatesCard({ studentId }) {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("corso");
  const [title, setTitle] = useState("");
  const { data: certs = [] } = useQuery({
    queryKey: ["student-certificates", studentId],
    queryFn: async () => (await api.get(`/students/${studentId}/certificates`)).data,
  });

  async function onUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!title.trim()) {
      toast.error("Inserisci il titolo dell'attestato (corso o seminario svolto)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("title", title.trim());
      await api.post(`/students/${studentId}/certificates`, fd);
      toast.success("Attestato caricato");
      setTitle("");
      qc.invalidateQueries({ queryKey: ["student-certificates", studentId] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  }

  const del = useMutation({
    mutationFn: async (certId) => (await api.delete(`/students/certificates/${certId}`)).data,
    onSuccess: () => { toast.success("Attestato eliminato"); qc.invalidateQueries({ queryKey: ["student-certificates", studentId] }); },
  });

  function openCert(c) {
    const token = localStorage.getItem("token");
    const base = process.env.REACT_APP_BACKEND_URL;
    openFile(`${base}/api/students/certificates/${c.id}/download?auth=${encodeURIComponent(token)}`, c.original_filename);
  }

  function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  const seminars = certs.filter((c) => c.kind === "seminario");
  const courses = certs.filter((c) => c.kind === "corso");

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none">
      <div className="p-4 border-b border-stone-100 space-y-3">
        <div className="text-sm font-medium">Attestati studente</div>
        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-2 md:items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Tipo</Label>
            <select
              data-testid="student-cert-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1C352D]/20"
            >
              <option value="corso">Corso</option>
              <option value="seminario">Seminario</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">
              Titolo {kind === "seminario" ? "seminario" : "corso"}
            </Label>
            <Input
              data-testid="student-cert-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              placeholder={kind === "seminario" ? "Es. Seminario Missione e Vocazione" : "Es. Sistematica 1"}
            />
          </div>
          <label className="cursor-pointer inline-flex items-center justify-center gap-2 rounded-full bg-[#1C352D] hover:bg-[#264A3E] text-white px-4 h-10 text-sm transition-colors duration-150">
            <Upload size={14}/> {uploading ? "Caricamento…" : "Carica attestato"}
            <input data-testid="student-cert-upload" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={onUpload} disabled={uploading}/>
          </label>
        </div>
        <div className="text-[11px] text-stone-500">
          Formati: PDF, PNG, JPG, JPEG, WEBP · Max 15 MB per file. Gli attestati saranno visibili allo studente nella sua area <em>Attestati</em>.
        </div>
      </div>

      <CertGroup title="Corsi" icon={<GraduationCap size={14}/>} accent="#15803D" items={courses} onOpen={openCert} onDelete={(c) => { if (window.confirm(`Eliminare "${c.title}"?`)) del.mutate(c.id); }} formatSize={formatSize}/>
      <CertGroup title="Seminari" icon={<Presentation size={14}/>} accent="#0369A1" items={seminars} onOpen={openCert} onDelete={(c) => { if (window.confirm(`Eliminare "${c.title}"?`)) del.mutate(c.id); }} formatSize={formatSize}/>
    </Card>
  );
}

function CertGroup({ title, icon, accent, items, onOpen, onDelete, formatSize }) {
  return (
    <div>
      <div className="px-4 py-2.5 border-b border-stone-100 bg-[#FDFBF7] flex items-center gap-2 text-[12px] font-medium" style={{ color: accent }}>
        {icon} {title} <span className="text-stone-400 font-normal">· {items.length}</span>
      </div>
      <div className="divide-y divide-stone-100">
        {items.length === 0 && (
          <div className="p-5 text-xs text-stone-400">Nessun attestato caricato.</div>
        )}
        {items.map((c) => (
          <div key={c.id} className="p-4 flex items-center gap-3" data-testid={`admin-cert-${c.id}`}>
            <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ background: `${accent}15`, color: accent }}>
              <Award size={16}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{c.title}</div>
              <div className="text-[11px] text-stone-500 truncate">
                {c.original_filename} · {formatSize(c.size)} · rilasciato {fmtDate(c.uploaded_at)}
              </div>
            </div>
            <button onClick={() => onOpen(c)} className="text-stone-500 hover:text-[#1C352D] p-2 rounded-full hover:bg-stone-50 transition-colors duration-150" title="Apri">
              <FileDown size={15}/>
            </button>
            <button onClick={() => onDelete(c)} className="text-stone-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors duration-150" title="Elimina">
              <Trash2 size={15}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
